// API Service for connecting to the backend
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';


// Get auth headers with token
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Handle unauthorized responses
const handleResponse = async (response) => {
  if (response.status === 401) {
    // Token expired or invalid - trigger logout
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.reload();
    throw new Error('Session expired. Please login again.');
  }
  return response;
};

// Fetch all agents with their properties
export const fetchAgents = async (page = 1, limit = 50, filters = {}) => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(filters.name && { name: filters.name }),
      ...(filters.officeName && { officeName: filters.officeName }),
      ...(filters.leadStatus && { leadStatus: filters.leadStatus }),
      ...(filters.city && { city: filters.city }),
      ...(filters.district && { district: filters.district }),
      ...(filters.neighbourhood && { neighbourhood: filters.neighbourhood }),
      ...(filters.website && { website: filters.website }),
      ...(filters.phoneNumber && { phoneNumber: filters.phoneNumber }),
      ...(filters.hasCallStatus && { hasCallStatus: filters.hasCallStatus })
    });
    
    let response = await fetch(`${API_BASE_URL}/agents?${queryParams}`, {
      headers: getAuthHeaders()
    });
    response = await handleResponse(response);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(' Error fetching agents:', error);
    throw error;
  }
};

// Fetch filter options
export const fetchFilters = async () => {
  try {
    let response = await fetch(`${API_BASE_URL}/filters`, {
      headers: getAuthHeaders()
    });
    response = await handleResponse(response);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching filters:', error);
    throw error;
  }
};

// Fetch all agent statuses
export const fetchAgentStatuses = async () => {
  try {
    let response = await fetch(`${API_BASE_URL}/agent-statuses`, {
      headers: getAuthHeaders()
    });
    response = await handleResponse(response);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching agent statuses:', error);
    return { success: false, data: {} };
  }
};

// Fetch call statuses (phone → status map from call_statuses collection)
export const fetchCallStatuses = async () => {
  try {
    let response = await fetch(`${API_BASE_URL}/elevenlabs/call-statuses`, {
      headers: getAuthHeaders()
    });
    response = await handleResponse(response);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    // Return full response with status, timestamps, callSuccessful, and conversationIds
    return { success: true, data: data.data, timestamps: data.timestamps, callSuccessful: data.callSuccessful, conversationIds: data.conversationIds };
  } catch (error) {
    console.error('Error fetching call statuses:', error);
    return { success: false, data: {}, timestamps: {}, callSuccessful: {} };
  }
};

// Update agent status and notes
export const updateAgentStatus = async (agentId, { leadStatus, notes }) => {
  try {
    let response = await fetch(`${API_BASE_URL}/agents/${encodeURIComponent(agentId)}/status`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ leadStatus, notes }),
    });
    response = await handleResponse(response);
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    console.error('Error updating agent status:', error);
    throw error;
  }
};

